import { Avatar, Button, Card, Divider, Space, Steps, Table, Typography } from 'antd';
import Title from 'antd/lib/typography/Title';
import type { Dayjs } from 'dayjs';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { DriverDrawerBodySkeleton } from '@/components/skeletons/DriverDrawerBodySkeleton';
import { AppDrawer } from '@/components/ui/AppDrawer';
import { getStatusColor } from '@/core/utils/get_status_color';
import type {
  GetDriversWithAddressByDateQuery,
  GetDriversWithLastTrackPointQuery,
} from '@/generated/graphql/graphql';
import dayjs from '@/utils/dayjs-instance';
import { POLLING_INTERVAL_MS } from '../../constants';
import { useGetClientPhone, useGetDriversWithAddressByDate } from '../../query/tracking.hooks';
import { getDayRange } from '../../utils/dateRange';
import {
  calculateEndTime,
  formatIsoTime,
  getExpressOrderColor,
  isExpressOrder,
} from '../../utils/expressOrdersUtils';
import type { OrderChange } from '../../utils/orderChange';
import { ExpressBadge } from '../ExpressBadge';
import { OrderScheduleInfo } from '../OrderScheduleInfo';
import { DriverMetrics } from './DriverMetrics';
import { OrderChangeTimeline } from './OrderChangeTimeline';

export type { OrderChange } from '../../utils/orderChange';

const formatIsoDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '';
  }
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (!match) {
    return isoString;
  }
  return `${match[1]} ${match[2]}`;
};

const { Text } = Typography;

interface SelectedOrderCoords {
  id: number;
  lat: number;
  lng: number;
}

interface DriverDrawerViewProps {
  selectedDriver: GetDriversWithLastTrackPointQuery['user'][0] | null;
  close: () => void;
  selectedDate: Dayjs;
  onOrderSelect: (order: SelectedOrderCoords | null) => void;
  onGeoEventNavigate?: (
    coords: { lat: number; lng: number },
    statusName: string,
    orderId: number
  ) => void;
  selectedOrderId?: number | null;
  orderChanges: ReadonlyArray<OrderChange> | undefined;
  isToday: boolean;
}

export const DriverDrawerView: React.FC<DriverDrawerViewProps> = ({
  close,
  selectedDriver,
  selectedDate,
  onOrderSelect,
  onGeoEventNavigate,
  selectedOrderId,
  orderChanges,
  isToday,
}) => {
  return (
    <AppDrawer
      onClose={close}
      rootStyle={{ position: 'absolute' }}
      title={
        <DriverDrawerHeader name={selectedDriver?.name || ''} login={selectedDriver?.email || ''} />
      }
      placement="left"
      open={Boolean(selectedDriver)}
      getContainer={false}
      mask={false}
      size={400}
    >
      {selectedDriver && (
        <DriverDrawerBody
          id={selectedDriver.id}
          selectedDate={selectedDate}
          onOrderSelect={onOrderSelect}
          onGeoEventNavigate={onGeoEventNavigate}
          selectedOrderId={selectedOrderId}
          orderChanges={orderChanges}
          isToday={isToday}
        />
      )}
    </AppDrawer>
  );
};

interface DriverDrawerBodyProps {
  id: string;
  selectedDate: Dayjs;
  onOrderSelect: (order: SelectedOrderCoords | null) => void;
  onGeoEventNavigate?: (
    coords: { lat: number; lng: number },
    statusName: string,
    orderId: number
  ) => void;
  selectedOrderId?: number | null;
  orderChanges: ReadonlyArray<OrderChange> | undefined;
  isToday: boolean;
}

const DriverDrawerBody: React.FC<DriverDrawerBodyProps> = ({
  id,
  selectedDate,
  onOrderSelect,
  onGeoEventNavigate,
  selectedOrderId,
  orderChanges,
  isToday,
}) => {
  const { selectedDepartmentId: departmentId, selectedDepartment } = useDepartments();
  const departmentTimeZone = selectedDepartment?.timeZone ?? 0;
  const { dateFrom, dateTo } = useMemo(() => getDayRange(selectedDate), [selectedDate]);
  const runtimeOptions = useMemo(
    () => ({ refetchInterval: isToday ? POLLING_INTERVAL_MS : (false as const) }),
    [isToday]
  );
  const { data, isPending, isLoading } = useGetDriversWithAddressByDate(
    {
      id,
      departmentId,
      dateFrom,
      dateTo,
    },
    runtimeOptions
  );
  const [selectedOrder, setSelectedOrder] = useState<
    GetDriversWithAddressByDateQuery['user'][0]['orders'][0] | null
  >(null);

  useEffect(() => {
    if (selectedOrderId && data?.user[0]?.orders) {
      const order = data.user[0].orders.find((o) => o.id === selectedOrderId);
      if (order) {
        setSelectedOrder(order);
      }
    } else if (!selectedOrderId) {
      setSelectedOrder(null);
    }
  }, [selectedOrderId, data]);

  if (isLoading || isPending) {
    return <DriverDrawerBodySkeleton />;
  }

  const driverChanges = orderChanges ?? [];
  const startTime = driverChanges.length > 0 ? driverChanges[0].createdAt : null;
  const lastActivity =
    driverChanges.length > 0 ? driverChanges[driverChanges.length - 1].createdAt : null;

  return (
    <>
      <DriverMetrics
        startTime={startTime}
        lastActivity={lastActivity}
        departmentTimeZone={departmentTimeZone}
      />
      {selectedOrder && data?.user[0]?.orders ? (
        <DriverOrder
          order={selectedOrder}
          onBack={() => setSelectedOrder(null)}
          departmentTimeZone={departmentTimeZone}
          orderChanges={driverChanges.filter((oc) => oc.orderId === selectedOrder.id)}
          onNavigateToCoords={(coords, statusName) => {
            if (onGeoEventNavigate) {
              onGeoEventNavigate(coords, statusName, selectedOrder.id);
            } else {
              onOrderSelect({ id: selectedOrder.id, ...coords });
            }
          }}
        />
      ) : (
        <DriverWayPoints
          orders={data?.user[0]?.orders ?? []}
          onClick={setSelectedOrder}
          onOrderSelect={onOrderSelect}
          departmentTimeZone={departmentTimeZone}
        />
      )}
    </>
  );
};

const DriverDrawerHeader: React.FC<{ name: string; login: string }> = ({ name, login }) => {
  return (
    <div style={{ display: 'flex', padding: 15 }}>
      <Avatar size={64} icon={name[0]} style={{ flexShrink: 0 }} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: 15,
        }}
      >
        <strong>{name}</strong>
        <span>{login}</span>
      </div>
    </div>
  );
};

const DriverWayPoints: React.FC<{
  orders: GetDriversWithAddressByDateQuery['user'][0]['orders'];
  onClick: React.Dispatch<
    React.SetStateAction<GetDriversWithAddressByDateQuery['user'][0]['orders'][0] | null>
  >;
  onOrderSelect: (order: { id: number; lat: number; lng: number } | null) => void;
  departmentTimeZone: number;
}> = ({ orders, onClick, onOrderSelect, departmentTimeZone }) => {
  const handleStepClick = (order: GetDriversWithAddressByDateQuery['user'][0]['orders'][0]) => {
    onClick(order);
    if (order.client?.address?.lat && order.client.address.lng) {
      onOrderSelect({
        id: order.id,
        lat: order.client.address.lat,
        lng: order.client.address.lng,
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Steps
        orientation="vertical"
        items={orders.map((el, i: number) => ({
          style: { cursor: 'pointer' },
          key: el.id,
          content: (
            <button
              type="button"
              onClick={() => handleStepClick(el)}
              style={{
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                padding: 0,
                width: '100%',
                textAlign: 'left',
              }}
            >
              <DrawerWayPoint waypoint={el} departmentTimeZone={departmentTimeZone} />
            </button>
          ),
          icon: <DrawerWayPointIcon id={i + 1} status={el.statusName ?? ''} />,
        }))}
      />
      <ProductsSummary orders={orders} />
    </div>
  );
};

const DrawerWayPointIcon: React.FC<{ id: number; status: string }> = ({ id, status }) => {
  const bgColor = getStatusColor(status);
  return (
    <span
      style={{
        background: bgColor,
        color: 'white',
        borderRadius: '50%',
        height: 30,
        width: 30,
        fontSize: 15,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {id}
    </span>
  );
};

type AggregatedProduct = {
  name: string;
  quantity: number;
};

const aggregateOrderProducts = (
  orders: GetDriversWithAddressByDateQuery['user'][0]['orders']
): AggregatedProduct[] => {
  const aggregation = new Map<string, number>();

  orders.forEach((order) => {
    order.positions.forEach((position) => {
      const productName = position.product.name?.trim();
      if (!productName) {
        return;
      }
      const currentQuantity = aggregation.get(productName) ?? 0;
      aggregation.set(productName, currentQuantity + position.quantity);
    });
  });

  return Array.from(aggregation.entries())
    .map(([name, quantity]) => ({ name, quantity }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const ProductsSummary: React.FC<{
  orders: GetDriversWithAddressByDateQuery['user'][0]['orders'];
}> = ({ orders }) => {
  const aggregatedProducts = useMemo(() => aggregateOrderProducts(orders), [orders]);

  if (!aggregatedProducts.length) {
    return null;
  }

  return (
    <div style={{ marginTop: 4 }}>
      <Title level={5} style={{ marginBottom: 8 }}>
        Products total for the day
      </Title>
      <Table
        className="app-table"
        size="small"
        pagination={false}
        columns={[
          { title: 'Product', dataIndex: 'name', key: 'name' },
          { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
        ]}
        dataSource={aggregatedProducts.map((product) => ({
          ...product,
          key: product.name,
        }))}
      />
    </div>
  );
};

const DrawerWayPoint: React.FC<{
  waypoint: GetDriversWithAddressByDateQuery['user'][0]['orders'][0];
  departmentTimeZone: number;
}> = ({ waypoint, departmentTimeZone }) => {
  const isExpress = isExpressOrder(waypoint);
  const expressColor = isExpress ? getExpressOrderColor(waypoint, departmentTimeZone) : null;
  const startTime = formatIsoTime(waypoint.plannedDate) || '-';
  const endTime =
    calculateEndTime(waypoint.plannedDate, waypoint.plannedDateDuration ?? undefined) || startTime;
  const createdAtText = waypoint.createdAt
    ? dayjs
        .utc(waypoint.createdAt)
        .utcOffset(departmentTimeZone * 60)
        .format('YYYY-MM-DD HH:mm')
    : formatIsoDateTime(waypoint.createdAt);

  return (
    <Space orientation="vertical" size={2}>
      <div>
        <Text>
          {waypoint.plannedDateDuration ? `R: ${startTime} - E: ${endTime}` : `R, E: ${startTime}`}
        </Text>
        <OrderScheduleInfo
          plannedDate={waypoint.plannedDate}
          plannedDateDuration={waypoint.plannedDateDuration ?? undefined}
          departmentTimeZone={departmentTimeZone}
          isExpress={isExpress}
          statusName={waypoint.statusName ?? undefined}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
        <Text strong>{waypoint.shortCode || `Order #${waypoint.id}`}</Text>
        {isExpress && expressColor && <ExpressBadge color={expressColor} />}
        <Text type="secondary" style={{ fontSize: '11px' }}>
          ({createdAtText})
        </Text>
      </div>
      <Text type="secondary">{waypoint.client.fullname}</Text>
    </Space>
  );
};

const DriverOrder: React.FC<{
  order: GetDriversWithAddressByDateQuery['user'][0]['orders'][0];
  onBack?: () => void;
  departmentTimeZone: number;
  orderChanges?: ReadonlyArray<OrderChange>;
  onNavigateToCoords?: (coords: { lat: number; lng: number }, statusName: string) => void;
}> = ({ order, onBack, departmentTimeZone, orderChanges, onNavigateToCoords }) => {
  const isExpress = isExpressOrder(order);
  const expressColor = isExpress ? getExpressOrderColor(order, departmentTimeZone) : null;

  return (
    <Card>
      <div
        style={{
          padding: '16px 0',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Button type="text" onClick={onBack} style={{ marginRight: 8 }}>
          ← Back
        </Button>
        <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{order.shortCode || `Order #${order.id}`}</span>
          {isExpress && expressColor && <ExpressBadge color={expressColor} />}
        </Title>
      </div>
      <OrderInfo order={order} departmentTimeZone={departmentTimeZone} />
      {orderChanges && orderChanges.length > 0 && (
        <>
          <Divider />
          <OrderChangeTimeline
            orderChanges={orderChanges}
            departmentTimeZone={departmentTimeZone}
            onNavigateToCoords={onNavigateToCoords}
          />
        </>
      )}
      <Divider />
      <OrderPosition positions={order.positions} total={order.totalCents} />
    </Card>
  );
};

const OrderInfo: React.FC<{
  order: GetDriversWithAddressByDateQuery['user'][0]['orders'][0];
  departmentTimeZone: number;
}> = ({ order, departmentTimeZone }) => {
  const isExpress = isExpressOrder(order);
  const startTime = formatIsoTime(order.plannedDate) || '-';
  const endTime =
    calculateEndTime(order.plannedDate, order.plannedDateDuration ?? undefined) || startTime;

  const [shouldFetchPhone, setShouldFetchPhone] = useState(false);

  const { data: phoneData, isFetching: isPhoneLoading } = useGetClientPhone({
    clientId: shouldFetchPhone ? order.client.id : 0,
  });

  const handlePhoneClick = () => {
    setShouldFetchPhone(true);
  };

  const clientPhone = phoneData?.getClientPhone?.phone;

  return (
    <Space orientation="vertical" size={1}>
      <div>
        <Text>
          {order.plannedDateDuration ? `R: ${startTime} - E: ${endTime}` : `R, E: ${startTime}`}
        </Text>
        <OrderScheduleInfo
          plannedDate={order.plannedDate}
          plannedDateDuration={order.plannedDateDuration ?? undefined}
          departmentTimeZone={departmentTimeZone}
          isExpress={isExpress}
          statusName={order.statusName ?? undefined}
        />
      </div>
      <br />
      <Text type="secondary">{order.statusName}</Text>
      <br />
      <Text strong type="warning">
        {order.client.fullname}
      </Text>
      {shouldFetchPhone && clientPhone ? (
        <Text type="secondary">Phone: {clientPhone}</Text>
      ) : (
        <Button
          onClick={handlePhoneClick}
          type="link"
          loading={isPhoneLoading}
          size="small"
          style={{ padding: 0, height: 'auto' }}
        >
          Click to see phone
        </Button>
      )}
      <br />
      <>Direction: -</>
    </Space>
  );
};

const OrderPosition: React.FC<{
  positions: GetDriversWithAddressByDateQuery['user'][0]['orders'][0]['positions'];
  total: number;
}> = ({ positions, total }) => {
  return (
    <>
      <Table
        className="app-table"
        columns={[
          { title: 'Name', dataIndex: 'name' },
          { title: 'Quantity', dataIndex: 'quantity' },
          { title: 'Price', dataIndex: 'totalPrice' },
        ]}
        dataSource={positions.map((p, index) => ({
          key: index,
          name: p.product.name,
          quantity: p.quantity,
          totalPrice: p.totalPrice,
        }))}
        pagination={false}
      />
      <Title level={3}>
        <Text type="warning">Total: </Text>
        {total ? total : '-'}
        {total ? '' : null}
      </Title>
    </>
  );
};
